import { urls } from "@selector/shared";
import { AlignmentDensityEnum, SequenceAnalyzerFeatureType } from "@selector/shared/openapi";
import { type FC, memo, type ReactNode, useCallback, useMemo, useState } from "react";
import type { Key } from "react-aria";
import {
  HeaderDivider,
  HStack,
  Item,
  MenuOptionItem,
  MenuTriggerButton,
  notify,
  Section,
  Spacer,
  SymbolIcon,
  TriggerButton,
  useResizableTabPanelsContext,
} from "widgets";

import { getSequenceViewerSessionIdFromFeatures, getSequenceViewerSessionIdFromRanges } from "../../api/fetch.ts";
import { MAX_FEATURES_FOR_SEQUENCE_VIEWER, MAX_RANGE_LENGTH_FOR_SEQUENCE_VIEWER } from "../../settings.ts";
import { useGenomeViewerStore } from "../../store/store.ts";
import type { SessionRequest } from "../../types.ts";
import { getMaxRangeLength } from "../../utils/graphics/get-max-length-range.ts";
import { routeWithBasename } from "../../utils/route-with-basename.ts";
import { TracksConfigurationDialog } from "../track-configuration/TracksConfiguration.tsx";
import { ExportButton } from "./export/ExportPopover.tsx";
import style from "./GenomeViewerToolbar.module.css";
import { SavePopover } from "./SavePopover.tsx";

export const GenomeViewerToolbar = () => {
  const noTracks = useGenomeViewerStore((state) => state.tracks.size === 0);

  return (
    <GenomeViewerToolbarContainer>
      <GenomeName />
      <Spacer />
      <TracksConfigurationDialog />
      <SelectionMenu isDisabled={noTracks} />
      <AlignmentMenu isDisabled={noTracks} />
      <ExportButton />
      <SavePopover />
      <HeaderDivider />
      <ToggleInspectionPanel />
    </GenomeViewerToolbarContainer>
  );
};

const SelectionMenu = ({ isDisabled }: { isDisabled: boolean }) => {
  // Selection
  const refreshMain = useGenomeViewerStore((state) => state.refreshMain);
  const fullRangeSelection = useGenomeViewerStore((state) => state.fullRangeSelection);
  const toggleFullRangeSelection = useGenomeViewerStore((state) => state.toggleFullRangeSelection);
  const clearRangeSelection = useGenomeViewerStore((state) => state.clearRangeSelection);
  const getSelectedRange = useGenomeViewerStore((state) => state.getSelectedRange);
  const getFeaturesSessionRequest = useGenomeViewerStore((state) => state.getFeaturesSessionRequest);

  const [featuresMap, setFeaturesMap] = useState<Map<SequenceAnalyzerFeatureType, SessionRequest>>();

  const handleOpen = useCallback(() => {
    const featuresSessionRequest = getFeaturesSessionRequest();
    if (featuresSessionRequest) {
      setFeaturesMap(featuresSessionRequest);
    }
  }, [setFeaturesMap, getFeaturesSessionRequest]);

  const selectedGenes = useMemo(() => featuresMap?.get(SequenceAnalyzerFeatureType.gene), [featuresMap]);
  const selectedProteinLocations = useMemo(
    () => featuresMap?.get(SequenceAnalyzerFeatureType.proteinLocation),
    [featuresMap],
  );
  const selectedTranscriptLocations = useMemo(
    () => featuresMap?.get(SequenceAnalyzerFeatureType.transcriptLocation),
    [featuresMap],
  );

  const sendFeaturesToSequenceViewer = useCallback(
    (featureType: SequenceAnalyzerFeatureType) => {
      const payload = featuresMap?.get(featureType);
      if (payload && payload.sequenceIds) {
        if (payload.sequenceIds.length <= MAX_FEATURES_FOR_SEQUENCE_VIEWER) {
          getSequenceViewerSessionIdFromFeatures(payload).then((sessionId) => {
            window.open(routeWithBasename(`/visualizer/sequence?sessionId=${sessionId}`, urls), "_blank");
          });
        } else {
          // Too much proteins are selected
          notify.warning({
            title: "Too Many Features Selected",
            subtitle: `You can select up to ${MAX_FEATURES_FOR_SEQUENCE_VIEWER.toLocaleString("en-US")} features.`,
            children: `Please reduce the number of selected features (${payload.sequenceIds?.length}).`,
          });
        }
      } else {
        // No selection
        notify.warning({
          title: "No Feature Selected",
          children: "Please select one or more features to view the sequence.",
        });
      }
    },
    [featuresMap],
  );

  const sendRangeToSequenceViewer = useCallback(() => {
    const rangeToSend = getSelectedRange();
    if (rangeToSend) {
      const maxRangeLength = getMaxRangeLength(rangeToSend);
      if (maxRangeLength <= MAX_RANGE_LENGTH_FOR_SEQUENCE_VIEWER) {
        getSequenceViewerSessionIdFromRanges(rangeToSend).then((sessionId) => {
          window.open(routeWithBasename(`/visualizer/sequence?sessionId=${sessionId}`, urls), "_blank");
        });
      } else {
        // Selection is too wide
        notify.warning({
          title: "Selection Too Wide",
          subtitle: `The maximum allowed range is ${MAX_RANGE_LENGTH_FOR_SEQUENCE_VIEWER.toLocaleString("en-US")} bases.`,
          children: "You can zoom in or make a narrower selection using Shift + drag with your mouse.",
        });
      }
    } else {
      // No selection. Should never happen as we send the visible area then
      notify.warning({
        title: "No Selection Made",
        children: "Please select a region to view the sequence.",
      });
    }
  }, [getSelectedRange]);

  const handleAction = (key: Key) => {
    switch (key) {
      case "select-in-all-tracks": {
        toggleFullRangeSelection();
        break;
      }
      case "clear-selection": {
        clearRangeSelection();
        break;
      }
      case "send-to-sequence-viewer": {
        sendRangeToSequenceViewer();
        break;
      }
      case "send-genes-to-sequence-viewer": {
        sendFeaturesToSequenceViewer(SequenceAnalyzerFeatureType.gene);
        break;
      }
      case "send-proteins-to-sequence-viewer": {
        sendFeaturesToSequenceViewer(SequenceAnalyzerFeatureType.proteinLocation);
        break;
      }
      case "send-transcripts-to-sequence-viewer": {
        sendFeaturesToSequenceViewer(SequenceAnalyzerFeatureType.transcriptLocation);
        break;
      }
    }

    refreshMain();
  };

  return (
    <MenuTriggerButton
      icon="arrow_outward"
      label="Selection"
      onOpenChange={(isOpen) => {
        if (isOpen) {
          handleOpen();
        }
      }}
      onAction={handleAction}
      showLabel
      isDisabled={isDisabled}
    >
      <Section title="Sequence Viewer">
        <Item key={"send-to-sequence-viewer"}>
          <MenuOptionItem
            icon="arrow_outward"
            title={"Send Range"}
            description={`Maximum ${MAX_RANGE_LENGTH_FOR_SEQUENCE_VIEWER.toLocaleString("en-US")} bp`}
          />
        </Item>

        {selectedGenes ? (
          <Item key={"send-genes-to-sequence-viewer"}>
            <MenuOptionItem
              icon="arrow_outward"
              title={`Send Genes (${selectedGenes.sequenceIds?.length})`}
              description={`Maximum ${MAX_FEATURES_FOR_SEQUENCE_VIEWER.toLocaleString("en-US")} genes`}
            />
          </Item>
        ) : null}

        {selectedProteinLocations ? (
          <Item key={"send-proteins-to-sequence-viewer"}>
            <MenuOptionItem
              icon="arrow_outward"
              title={`Send Proteins (${selectedProteinLocations.sequenceIds?.length})`}
              description={`Maximum ${MAX_FEATURES_FOR_SEQUENCE_VIEWER.toLocaleString("en-US")} proteins`}
            />
          </Item>
        ) : null}

        {selectedTranscriptLocations ? (
          <Item key={"send-transcripts-to-sequence-viewer"}>
            <MenuOptionItem
              icon="arrow_outward"
              title={`Send Transcripts (${selectedTranscriptLocations.sequenceIds?.length})`}
              description={`Maximum ${MAX_FEATURES_FOR_SEQUENCE_VIEWER.toLocaleString("en-US")} transcripts`}
            />
          </Item>
        ) : null}
      </Section>
      <Section>
        <Item key="select-in-all-tracks">
          <MenuOptionItem
            title={"Select in All Tracks"}
            icon="select_all"
            tag={fullRangeSelection ? <SymbolIcon icon="check" /> : undefined}
          />
        </Item>
        <Item key="clear-selection">
          <MenuOptionItem title={"Clear Selection"} icon="deselect" shortcut={"C"} />
        </Item>
      </Section>
    </MenuTriggerButton>
  );
};

const AlignmentMenu = ({ isDisabled }: { isDisabled: boolean }) => {
  const density = useGenomeViewerStore((state) => state.alignmentDensity);
  const setDensity = useGenomeViewerStore((state) => state.setAlignmentDensity);
  const resetCanvas = useGenomeViewerStore((state) => state.resetAlignments);
  const centerAlignment = useGenomeViewerStore((state) => state.centerAlignment);
  const alignedFamily = useGenomeViewerStore((state) => state.alignedFamily);
  const refreshMain = useGenomeViewerStore((state) => state.refreshMain);

  const handleAction = (key: Key) => {
    switch (key) {
      case "draw-alignments-single": {
        setDensity(density === AlignmentDensityEnum.SINGLE ? AlignmentDensityEnum.OFF : AlignmentDensityEnum.SINGLE);
        break;
      }
      case "draw-alignments-low": {
        setDensity(density === AlignmentDensityEnum.LOW ? AlignmentDensityEnum.OFF : AlignmentDensityEnum.LOW);
        break;
      }
      case "reset-alignments": {
        resetCanvas();
        break;
      }
      case "center-active-alignment": {
        centerAlignment();
        break;
      }
    }

    refreshMain();
  };

  return (
    <MenuTriggerButton
      icon="align_horizontal_left"
      label="Alignment"
      showLabel
      isDisabled={isDisabled}
      badge={!!alignedFamily && !isDisabled}
      onAction={handleAction}
      disabledKeys={alignedFamily == undefined ? ["center-active-alignment", "reset-alignments"] : undefined}
    >
      <Section title="Alignment">
        <Item key="center-active-alignment">
          <MenuOptionItem title="Center Active Alignment" icon="align_horizontal_center" />
        </Item>
        <Item key="reset-alignments">
          <MenuOptionItem title="Reset Alignments" icon="reset_focus" />
        </Item>
      </Section>
      <Section>
        <Item key="draw-alignments-single">
          <MenuOptionItem
            title={"Active Alignment"}
            icon="pen_size_3"
            tag={density === AlignmentDensityEnum.SINGLE ? <SymbolIcon icon="check" /> : undefined}
          />
        </Item>
        <Item key="draw-alignments-low">
          <MenuOptionItem
            title={"All Alignments"}
            icon="stroke_full"
            tag={density === AlignmentDensityEnum.LOW ? <SymbolIcon icon="check" /> : undefined}
          />
        </Item>
      </Section>
    </MenuTriggerButton>
  );
};

export const GenomeViewerToolbarContainer: FC<{ children?: ReactNode }> = ({ children }) => {
  return (
    <HStack className={style.toolbar} data-testid="genome-viewer-toolbar">
      {children}
    </HStack>
  );
};

const GenomeName = memo(function LargeToolbar() {
  const loadedSyntenyOrganism = useGenomeViewerStore((state) => state.syntenyOrganism.name);

  return loadedSyntenyOrganism !== undefined && <div className={style.session_name}>{loadedSyntenyOrganism}</div>;
});

export const ToggleInspectionPanel = () => {
  const { isOpen, togglePanel } = useResizableTabPanelsContext();

  return (
    <TriggerButton
      onPress={() => {
        togglePanel();
      }}
      label="Toggle Inspection Panel"
      icon={<SymbolIcon icon={isOpen ? "right_panel_close" : "right_panel_open"} filled={isOpen} />}
      data-testid="toggle-inspection-panel"
    />
  );
};
